import {
  type DocumentData,
  type Firestore,
  collection,
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
  updateDoc,
} from 'firebase/firestore';
import {
  FormSchema,
  FormCreateInputSchema,
  FormUpdateInputSchema,
  type Form,
  type FormCreateInput,
  type FormUpdateInput,
} from '@taki/data-model';
import { CURRENT_SCHEMA_VERSION, buildUpdateData, createConverter } from './standard-fields.js';
import { type CountOptions, type ListOptions, runCount, runList } from './list-query.js';
import { createArchiveOps } from './archive-ops.js';

/**
 * Repository de la famille `form` (formulaire versionné, org-wide en v0).
 *
 * Réplique le PATRON `asset` : l'entrée est VALIDÉE par Zod
 * (`FormCreateInputSchema`) AVANT toute écriture Firestore. Les champs standard
 * sont remplis par le SDK, les requêtes `list` sont cadrées par orgId.
 *
 * Sécurité / RGPD : org-wide en v0 (même doctrine qu'asset). Le contenu collecté
 * (`answers`) ne transite PAS ici : potentiellement de la donnée de santé (RGPD
 * art. 9), il ira dans un sous-document privé owner + rôle désigné après v0 (cf.
 * @taki/data-model). Le champ `sensitivity` classe le questionnaire ; l'accès
 * fin (cloisonnement des sujets sensibles et mineurs) viendra ensuite.
 */

export type { FormCreateInput };

/** Nom de la collection Firestore de la famille form. */
export const FORMS_COLLECTION = 'forms';

/**
 * Repository typé pour la famille `form`.
 * Toute lecture est validée par `FormSchema`, toute écriture par
 * `FormCreateInputSchema` (cage IA : aucun accès Firestore direct hors SDK).
 */
export interface FormRepository {
  create(input: FormCreateInput): Promise<Form>;
  update(id: string, patch: FormUpdateInput): Promise<Form>;
  list(options?: ListOptions): Promise<Form[]>;
  count(options?: CountOptions): Promise<number>;
  get(id: string): Promise<Form | null>;
  /** Archive (`archivedAt`). Semantique commune : cf. archive-ops.ts. */
  archive(id: string): Promise<Form>;
  /** Desarchive (`archivedAt` = null, JAMAIS deleteField). Cf. archive-ops.ts. */
  unarchive(id: string): Promise<Form>;
  /** Supprime (`deletedAt` + `archivedAt`), invisible cote client. Cf. archive-ops.ts. */
  remove(id: string): Promise<Form>;
}

const formConverter = createConverter<Form>((raw: DocumentData) => FormSchema.parse(raw));

/**
 * @param getActorId - retourne l'uid de l'utilisateur authentifié courant
 *   (injecté par `createTakiSdk`). Les Security Rules exigent
 *   createdBy/updatedBy == request.auth.uid.
 */
export function createFormRepository(
  db: Firestore,
  getActorId: () => string,
  orgId: string,
): FormRepository {
  const col = collection(db, FORMS_COLLECTION).withConverter(formConverter);
  // Archiver, desarchiver, supprimer : la semantique des trois etats vit dans
  // `createArchiveOps` (archive-ops.ts), ecrite une seule fois pour les 55
  // familles. Une famille n'a plus rien a decider ici.
  const archiveOps = createArchiveOps<Form>(db, col, FORMS_COLLECTION, getActorId);

  return {
    async create(input: FormCreateInput): Promise<Form> {
      // Valider AVANT d'écrire : une entrée invalide échoue ici, rien n'atteint
      // Firestore. (Même doctrine que le patron asset.)
      const fields = FormCreateInputSchema.parse(input);

      const actor = getActorId();
      const ref = doc(col);
      const now = new Date();

      // Champs métier optionnels : on ne les écrit que s'ils sont fournis
      // (évite d'ancrer des `undefined` / `null` parasites dans Firestore).
      const optional: DocumentData = {};
      if (fields.templateSchema !== undefined) optional['templateSchema'] = fields.templateSchema;
      if (fields.templateVersion !== undefined) optional['templateVersion'] = fields.templateVersion;
      if (fields.bookingId !== undefined) optional['bookingId'] = fields.bookingId;
      if (fields.eventId !== undefined) optional['eventId'] = fields.eventId;
      if (fields.custodyId !== undefined) optional['custodyId'] = fields.custodyId;
      if (fields.reviewId !== undefined) optional['reviewId'] = fields.reviewId;
      if (fields.consentId !== undefined) optional['consentId'] = fields.consentId;
      if (fields.submittedByContactId !== undefined) optional['submittedByContactId'] = fields.submittedByContactId;
      if (fields.submittedAt !== undefined) optional['submittedAt'] = fields.submittedAt;
      if (fields.customFields !== undefined) optional['customFields'] = fields.customFields;

      const payload: DocumentData = {
        orgId: fields.orgId,
        type: fields.type,
        sensitivity: fields.sensitivity,
        ...optional,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: actor,
        updatedBy: actor,
        archivedAt: null,
        deletedAt: null,
        schemaVersion: CURRENT_SCHEMA_VERSION,
      };

      await setDoc(ref, payload as unknown as Form);

      // setDoc ne résout pas serverTimestamp() : on valide une projection
      // cohérente avec ce qui vient d'être écrit.
      return FormSchema.parse({
        id: ref.id,
        ...payload,
        createdAt: now,
        updatedAt: now,
      });
    },

    async update(id: string, patch: FormUpdateInput): Promise<Form> {
      // Valider AVANT d'écrire : même doctrine que create.
      const fields = FormUpdateInputSchema.parse(patch);
      const actor = getActorId();

      // Seules les clés réellement fournies sont écrites (patch partiel, merge).
      // Patch transforme pour updateDoc : customFields est eclate en chemins
      // pointes ('customFields.<cle>') pour fusionner cle par cle au lieu de
      // remplacer la map entiere (cf. buildUpdateData ; null = deleteField).
      const data = buildUpdateData(fields);
      data['updatedAt'] = serverTimestamp();
      data['updatedBy'] = actor;

      // Ref brute (sans converter) pour l'écriture partielle : une ref convertie
      // exigerait le document complet.
      await updateDoc(doc(db, FORMS_COLLECTION, id), data);

      // Relire via la ref convertie : le document réel est la source de vérité,
      // et le converter le valide avec FormSchema.
      const snap = await getDoc(doc(col, id));
      if (!snap.exists()) {
        throw new Error(`form ${id} introuvable après mise à jour.`);
      }
      return snap.data();
    },

    async list(options?: ListOptions): Promise<Form[]> {
      // Cadrage orgId, exclusion des archives, sous-type, tri, taille de page
      // et curseur : toute la semantique vit dans `runList` (list-query.ts),
      // ecrite une seule fois pour les 55 familles.
      return runList(col, orgId, options);
    },

    async count(options?: CountOptions): Promise<number> {
      // Compter sans charger : meme cadrage que `list`, sans page ni tri
      // (cf. runCount, list-query.ts). Un compteur calcule sur la page
      // courante ment des que la liste est paginee.
      return runCount(col, orgId, options);
    },

    async get(id: string): Promise<Form | null> {
      const ref = doc(col, id);
      const snap = await getDoc(ref);
      if (!snap.exists()) return null;
      return snap.data();
    },

    // archive / unarchive / remove : cf. `archiveOps` plus haut.
    ...archiveOps,
  };
}
